import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
// ADV-1171: /warranties is the obligations register (ADR-0047) — the LIABILITY
// view sourced from finance.warranties. Warranty remediation work has moved to
// /warranty-claims/:id (and surfaces under the obligation's Claims tab).
import { useWarranties } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "display_id",
    header: "Warranty",
    enableSorting: false,
    cell: ({ row }) => {
      const id = row.original.display_id as string | undefined;
      return (
        <span className="typo-mono font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
          {id || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "term_name",
    header: "Term",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.term_name as string | undefined;
      const category = row.original.term_category as string | undefined;
      if (!name && !category) {
        return <span className="text-[color:var(--color-text-lo)]">—</span>;
      }
      return (
        <span className="flex flex-col">
          <span className="typo-body text-[color:var(--color-text-hi)]">
            {name || "—"}
          </span>
          {category && (
            <span className="typo-label text-[color:var(--color-text-lo)]">
              {titleCase(category)}
            </span>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status as string | undefined;
      return status ? (
        <StatusBadge status={status} />
      ) : (
        <span className="text-[color:var(--color-text-lo)]">—</span>
      );
    },
  },
  {
    accessorKey: "effective_start_date",
    header: "Effective",
    cell: ({ row }) => {
      const value = row.original.effective_start_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "expiration_date",
    header: "Expires",
    cell: ({ row }) => {
      const value = row.original.expiration_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "is_transferable",
    header: "Transferable",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.is_transferable as boolean | null | undefined;
      if (value === true) {
        return (
          <span className="typo-label text-[color:var(--color-text-md)]">
            Transferable
          </span>
        );
      }
      return <span className="text-[color:var(--color-text-lo)]">—</span>;
    },
  },
];

export default function V2WarrantiesPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "effective_start_date", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useWarranties({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const warranties = (data?.warranties ?? []) as Row[];
  const total = data?.total ?? warranties.length;

  return (
    <ListPage
      title="Warranties"
      subtitle={`${total.toLocaleString()} obligations on record`}
    >
      <DataTable
        columns={columns}
        data={warranties}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/warranties/${row.id}`)}
        emptyMessage="No warranty obligations yet"
        emptyDescription="Obligations appear here when a completed install stamps a warranty term onto the customer."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
